import React from 'react';
import { useDispatch } from 'react-redux';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import RowActionButton, {
  MenuKeyType,
} from 'commons/components/RowActionButton/RowActionButton.component';
import { DELIVERY_NOTE_PRINTED_STATUS_MAP } from 'commons/constants';
import {
  DeliveryNoteListRowAction,
  DeliveryNoteStatusEnum,
} from 'commons/enums';
import { IDeliveryNote } from 'commons/types';
import { deliveryNotePrintPDF } from 'commons/utils/printDeliveryNotePDF.util';
import { cancelDeliveryNoteAction } from 'redux-stores/actions/dashboard.action';
import { printDeliveryNoteAction } from 'redux-stores/actions/deliveryNote.action';
import { setDeliveryNoteStatus } from 'redux-stores/reducers/dashboard.reducer';
import { AppDispatch } from 'redux-stores/store';

const DeliveryNoteRowActionBtn: React.FC<{
  deliveryNote: IDeliveryNote;
}> = ({ deliveryNote }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = React.useState(false);
  const [openCancelConfirm, setOpenCancelConfirm] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const options: {
    label: string;
    key: MenuKeyType;
    disabled?: boolean;
  }[] = [
    {
      label: 'Cancel Delivery Note',
      key: DeliveryNoteListRowAction.CANCEL_DELIVERY_NOTE,
      disabled: deliveryNote.status === DeliveryNoteStatusEnum.CANCELLED,
    },
    {
      label: 'Print Delivery Note',
      key: DeliveryNoteListRowAction.PRINT_DELIVERY_NOTE,
      disabled: !DELIVERY_NOTE_PRINTED_STATUS_MAP[deliveryNote.status],
    },
  ];

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const handleMenuClick = async (menuKey: MenuKeyType) => {
    switch (menuKey) {
      case DeliveryNoteListRowAction.CANCEL_DELIVERY_NOTE:
        setOpenCancelConfirm(true);
        break;
      case DeliveryNoteListRowAction.PRINT_DELIVERY_NOTE: {
        const fullDeliveryNote = await dispatch(
          printDeliveryNoteAction(deliveryNote.awb_number),
        ).unwrap();
        if (fullDeliveryNote) {
          const printedStatus =
            DELIVERY_NOTE_PRINTED_STATUS_MAP[deliveryNote.status];
          if (printedStatus) {
            dispatch(
              setDeliveryNoteStatus({
                awbNumber: deliveryNote.awb_number,
                status: printedStatus,
              }),
            );
          }
          await deliveryNotePrintPDF(fullDeliveryNote);
        }
        break;
      }
      default:
    }
    setOpen(false);
  };

  const handleConfirmCancel = () => {
    dispatch(cancelDeliveryNoteAction(deliveryNote.awb_number));
  };

  return (
    <>
      <RowActionButton
        open={open}
        options={options}
        handleClose={handleClose}
        handleMenuClick={handleMenuClick}
        handleToggle={handleToggle}
      />
      <ConfirmationDialog
        title="Cancel Delivery Note"
        content={`Are you sure you want to cancel delivery note <b>${deliveryNote.awb_number}</b>?`}
        openModal={openCancelConfirm}
        onClose={() => setOpenCancelConfirm(false)}
        onConfirm={handleConfirmCancel}
      />
    </>
  );
};

export default DeliveryNoteRowActionBtn;
